import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {object} CartAtelierUpgradesRefs
 * @property {HTMLElement} status - Polite status for saves and copies.
 */

/**
 * "Complete your heirloom" in the cart drawer. Each option toggles a cart attribute (shown on the
 * order) through /cart/update.js; the pair-commission button copies its discount code.
 *
 * @extends Component<CartAtelierUpgradesRefs>
 */
class CartAtelierUpgrades extends Component {
  requiredRefs = ['status'];

  /** @type {AbortController | null} */
  #abortController = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @param {Event} event */
  async toggle(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.attribute) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
        ...fetchConfig('json', {
          body: JSON.stringify({ attributes: { [input.dataset.attribute]: input.checked ? input.value : '' } }),
        }),
        signal: this.#abortController.signal,
      });
      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
      this.#announce(this.dataset.saved ?? '');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      input.checked = !input.checked;
      this.#announce(this.dataset.error ?? '');
    }
  }

  /** @param {Event} event */
  async copy(event) {
    const button = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const code = button?.dataset.code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      this.#announce(`${this.dataset.copied ?? ''} ${code}`.trim());
    } catch {
      this.#announce(code);
    }
  }

  /** @param {string} message */
  #announce(message) {
    this.refs.status.textContent = message;
  }
}

if (!customElements.get('cart-atelier-upgrades')) {
  customElements.define('cart-atelier-upgrades', CartAtelierUpgrades);
}
